import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { UploadCloud, FileText, Image as ImageIcon, FileSpreadsheet, Trash2, Download } from "lucide-react";
import { toast } from "sonner";

function iconFor(ctype = "", filename = "") {
  const ext = (filename.split(".").pop() || "").toLowerCase();
  if (ctype.startsWith("image/") || ["png", "jpg", "jpeg", "webp", "gif"].includes(ext))
    return ImageIcon;
  if (["csv", "xlsx", "xls"].includes(ext) || ctype.includes("spreadsheet")) return FileSpreadsheet;
  return FileText;
}
function humanSize(n) {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0; let x = n;
  while (x >= 1024 && i < u.length - 1) { x /= 1024; i++; }
  return `${x.toFixed(x < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

export default function Attachments({ taskId, clientId, compact = false }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    const params = {};
    if (taskId) params.task_id = taskId;
    else if (clientId) params.client_id = clientId;
    const { data } = await api.get("/files", { params });
    setFiles(data);
  }, [taskId, clientId]);

  useEffect(() => { load(); }, [load]);

  const uploadFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setBusy(true);
    try {
      for (const f of fileList) {
        const fd = new FormData();
        fd.append("file", f);
        const qs = new URLSearchParams();
        if (taskId) qs.set("task_id", taskId);
        if (clientId) qs.set("client_id", clientId);
        await api.post(`/files/upload?${qs.toString()}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`${fileList.length} file(s) uploaded`);
      await load();
    } catch (e) {
      toast.error("Upload failed", { description: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const remove = async (f) => {
    if (!window.confirm(`Remove ${f.original_filename}?`)) return;
    await api.delete(`/files/${f.id}`);
    toast.success("Removed");
    load();
  };

  const downloadHref = (f) => {
    const t = localStorage.getItem("strusst_token") || "";
    return `${API_BASE}/files/${f.id}/download?token=${encodeURIComponent(t)}`;
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    uploadFiles(e.dataTransfer.files);
  };

  return (
    <div data-testid={`attachments-${taskId || clientId || "root"}`} className="space-y-3">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed cursor-pointer p-4 text-center transition-colors ${
          dragOver ? "border-zinc-900 bg-zinc-50" : "border-zinc-300 hover:border-zinc-500 hover:bg-zinc-50"
        }`}
      >
        <input
          ref={inputRef}
          data-testid="attachment-file-input"
          type="file"
          multiple
          className="hidden"
          onChange={(e) => uploadFiles(e.target.files)}
        />
        <UploadCloud size={22} strokeWidth={1.5} className="mx-auto text-zinc-500" />
        <div className="text-sm font-medium mt-2">
          {busy ? "Uploading…" : "Click or drop files to upload"}
        </div>
        <div className="text-[11px] text-zinc-500 mt-1">
          PDF, images, spreadsheets, docs · max 15 MB per file
        </div>
      </div>

      {files.length > 0 ? (
        <ul className={`divide-y divide-zinc-200 border border-zinc-200 ${compact ? "" : ""}`}>
          {files.map((f) => {
            const Icon = iconFor(f.content_type, f.original_filename);
            return (
              <li key={f.id} data-testid={`file-row-${f.id}`} className="flex items-center gap-3 px-3 py-2 bg-white hover:bg-zinc-50 transition-colors">
                <Icon size={18} strokeWidth={1.6} className="text-zinc-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{f.original_filename}</div>
                  <div className="text-[11px] text-zinc-500 tabular">
                    {humanSize(f.size)} · {new Date(f.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })} · {f.uploaded_by}
                  </div>
                </div>
                <a
                  data-testid={`file-download-${f.id}`}
                  href={downloadHref(f)}
                  target="_blank"
                  rel="noreferrer"
                  className="p-1.5 hover:bg-zinc-200 rounded transition-colors"
                  title="Download"
                >
                  <Download size={15} />
                </a>
                <button
                  data-testid={`file-delete-${f.id}`}
                  onClick={() => remove(f)}
                  className="p-1.5 hover:bg-rose-50 rounded transition-colors"
                  title="Remove"
                >
                  <Trash2 size={15} className="text-rose-600" />
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="text-xs text-zinc-500">No files yet.</div>
      )}
    </div>
  );
}
